import IParser from "@/types/IParser"
import ParseError from "@/types/ParseError"
import dayjs from "dayjs"
import { Transaction } from "./transaction"
import { ITransaction } from "./transaction.types"
import { safeDayjs } from "@/helpers/dateHelper"
import {
  AccountTransaction,
  AccountTransactionStatus as TransactionStatusEnum,
  AccountTransactionType as TransactionTypeEnum,
} from "@/api/backofficeRest/generated/model"

export type TransactionStatusText =
  "Pending" | "Accepted" | "Rejected" | "Authorising"

export type ITransactionRaw = AccountTransaction

// The gateway emits PascalCase enum members while the whole FE — icon map, type
// filter, row-click routing, modal heading — is keyed on the spaced labels ark
// returned. Normalising here keeps every downstream consumer untouched.
//
// The divergence originates in be-ac-junifer's sync, which is also the proof of
// the inverse mapping used below —
// BillingAccountTransactionSyncProcessor.correctBillingAccountTransactionType:
//   if (type == "PAYG Transfer") "PaygTransfer" else type.replace(" ", "")
//
// Ten of these fourteen were verified directly against ark across seven staging
// accounts. Deposit, DunningCharge, CreditWriteOff and WriteOn (marked below)
// have no live rows anywhere in staging and take the de-spaced form by
// inference. Record<> over the enum is exhaustive, so a new gateway member
// fails type-check rather than leaking PascalCase into the Type column.
// orval's generated type folds field-level nullability into the enum type
// itself (`| null`), which Record's key constraint rejects — exclude it here.
const TRANSACTION_TYPE_LABELS: Record<
  Exclude<TransactionTypeEnum, null>,
  string
> = {
  [TransactionTypeEnum.AccountCredit]: "Account Credit",
  [TransactionTypeEnum.AccountDebit]: "Account Debit",
  [TransactionTypeEnum.Bill]: "Bill",
  [TransactionTypeEnum.Credit]: "Credit",
  [TransactionTypeEnum.CreditWriteOff]: "Credit Write Off", // inferred
  [TransactionTypeEnum.Deposit]: "Deposit", // inferred
  [TransactionTypeEnum.DunningCharge]: "Dunning Charge", // inferred
  [TransactionTypeEnum.IncomingBalanceTransfer]: "Incoming Balance Transfer",
  [TransactionTypeEnum.OutgoingBalanceTransfer]: "Outgoing Balance Transfer",
  [TransactionTypeEnum.PaygTransfer]: "PAYG Transfer", // the one startCase gets wrong
  [TransactionTypeEnum.Payment]: "Payment",
  [TransactionTypeEnum.Repayment]: "Repayment",
  [TransactionTypeEnum.WriteOff]: "Write Off",
  [TransactionTypeEnum.WriteOn]: "Write On", // inferred
}

const parseTransactionType = (
  type: TransactionTypeEnum | null | undefined,
): string => (type ? TRANSACTION_TYPE_LABELS[type] : "")

// `All` is a filter-only member of the gateway enum and never appears on a
// stored transaction; the other four are byte-identical to the FE's vocabulary.
// Mapping rather than casting keeps the nominal enum out of the string-literal
// union.
const TRANSACTION_STATUS_LABELS: Record<
  Exclude<TransactionStatusEnum, null>,
  TransactionStatusText
> = {
  [TransactionStatusEnum.Accepted]: "Accepted",
  [TransactionStatusEnum.Authorising]: "Authorising",
  [TransactionStatusEnum.Pending]: "Pending",
  [TransactionStatusEnum.Rejected]: "Rejected",
  [TransactionStatusEnum.All]: "Pending",
}

const parseTransactionStatus = (
  status: TransactionStatusEnum | null | undefined,
): TransactionStatusText =>
  status ? TRANSACTION_STATUS_LABELS[status] : "Pending"

export const TransactionParser: IParser<ITransaction, ITransactionRaw> = {
  toDomain: (data: ITransactionRaw) => {
    try {
      return new Transaction(
        Number(data.id),
        parseTransactionType(data.type),
        parseTransactionStatus(data.status),
        data.reference ?? "",
        dayjs(data.createdDttm),
        data.billId != null ? Number(data.billId) : undefined,
        safeDayjs(data.acceptedDttm ?? undefined),
        data.creditAmount ?? undefined,
        data.debitAmount ?? undefined,
        data.accountBalance ?? undefined,
        data.description ?? undefined,
        data.accountCreditId != null ? String(data.accountCreditId) : "",
      )
    } catch (error) {
      throw new ParseError(error)
    }
  },
}
